import React, { useMemo, useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import api from '../services/api';

const tabs = [
  { id: 'vue-global', label: 'Vue globale' },
  { id: 'taches', label: 'Tâches du jour' },
  { id: 'presence', label: 'Présence & pointage' },
  { id: 'performance', label: 'Performance' },
  { id: 'objectifs', label: 'Objectifs' },
  { id: 'projets', label: '📋 Projets' },
  { id: 'alertes', label: 'Alertes' },
];

const styles = {
  container: { minHeight: '100vh', background: '#060b16', color: '#fff' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', background: '#111f30', borderBottom: '1px solid rgba(255,255,255,.08)', position: 'sticky', top: 0, zIndex: 10 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  tlogo: { width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#C9A84C,#a8863c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 },
  navTabs: { display: 'flex', gap: 8, flexWrap: 'wrap', padding: '16px 24px', background: '#0e1724', borderBottom: '1px solid rgba(255,255,255,.07)', position: 'sticky', top: 74, zIndex: 9 },
  tab: { padding: '10px 16px', borderRadius: 999, background: 'rgba(255,255,255,.04)', color: '#9ca3af', cursor: 'pointer' },
  activeTab: { background: 'rgba(201,168,76,.22)', color: '#fff' },
  content: { padding: '24px' },
  kpiStrip: { display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 14, marginBottom: 24 },
  kpi: { background: '#0f1c2f', border: '1px solid rgba(255,255,255,.06)', borderRadius: 16, padding: 18 },
  kpiIcon: { fontSize: 20 },
  kpiValue: { fontSize: 26, fontWeight: 700 },
  filterButton: { padding: '8px 12px', borderRadius: 999, border: '1px solid rgba(255,255,255,.1)', background: 'transparent', color: '#bfc5d6', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#0f172d', borderRadius: 16, overflow: 'hidden' },
  th: { textAlign: 'left', padding: '12px 14px', fontSize: 11, color: '#677891', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,.08)' },
  td: { padding: '14px 14px', fontSize: 12, color: '#cbd5e1', borderBottom: '1px solid rgba(255,255,255,.05)' },
  sectionCard: { background: '#0f1c2f', border: '1px solid rgba(255,255,255,.06)', borderRadius: 16, padding: 20, marginBottom: 20 },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 16 },
};

function Pbar({ pct }) {
  const c = pct >= 70 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#ef5350';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,.08)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: c }} />
      </div>
      <span style={{ fontSize: 10, color: '#778', width: 28 }}>{pct}%</span>
    </div>
  );
}

function TdbEmployesPage() {
  const [activeTab, setActiveTab] = useState('vue-global');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.employees.getAll({ departement_id: 2 });
        setEmployees(response.employees || []);
        setData({ stats: {} });
      } catch (err) {
        console.error('Erreur:', err);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const date = useMemo(
    () => new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }),
    []
  );

  const stats = data?.stats || {};
  const kpis = [
    { label: 'Collaborateurs', value: String(stats.total_employees || employees.length), icon: '👥' },
    { label: 'Présents', value: String(stats.present_today ?? employees.filter(e => e.present).length), icon: '✅' },
    { label: 'Retards', value: String(stats.retards_today || 0), icon: '⏰' },
    { label: 'Score moyen', value: employees.length > 0 ? (employees.reduce((s, e) => s + (e.score || 0), 0) / employees.length).toFixed(1) : '—', icon: '⭐' },
    {
      label: 'Objectifs',
      value: (() => {
        const all = employees.flatMap(e => e.objectifs || []);
        return all.length > 0 ? Math.round(all.filter(o => o.statut === 'atteint').length / all.length * 100) + '%' : '—';
      })(),
      icon: '🎯'
    },
    { label: 'Alertes', value: String(employees.filter(e => !e.present || e.score < 6).length), icon: '⚠️' },
  ];

  const retardsList = employees.filter(e => e.present && e.arrivee && e.arrivee !== '—' && e.arrivee > (e.heureLimite || '08:30'));
  const absents = employees.filter(e => !e.present);
  const alertes = employees.filter(e => !e.present || e.score < 6);

  return (
    <MainLayout>
      <div style={styles.container}>
        <div style={styles.topbar}>
          <div style={styles.topLeft}>
            <div style={styles.tlogo}>👥</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Tableau de Bord RH</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Vue centralisée du personnel et du suivi</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 11, padding: '6px 12px', background: '#0f1b2b', borderRadius: 20, border: '1px solid rgba(255,255,255,.08)' }}>{date}</div>
            <button style={styles.filterButton}>↻ Actualiser</button>
          </div>
        </div>

        <div style={styles.navTabs}>
          {tabs.map((tab) => (
            <div key={tab.id} style={{ ...styles.tab, ...(activeTab === tab.id ? styles.activeTab : {}) }} onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </div>
          ))}
        </div>

        <div style={styles.content}>
          <div style={styles.kpiStrip}>
            {kpis.map((kpi) => (
              <div key={kpi.label} style={styles.kpi}>
                <div style={styles.kpiIcon}>{kpi.icon}</div>
                <div style={styles.kpiValue}>{kpi.value}</div>
                <div style={{ fontSize: 11, color: '#8ea0b7', marginTop: 6 }}>{kpi.label}</div>
              </div>
            ))}
          </div>

          {/* VUE GLOBALE */}
          {activeTab === 'vue-global' && (
            <div style={styles.sectionCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Vue globale du personnel</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{loading ? 'Chargement...' : `${employees.length} collaborateurs`}</div>
              </div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#667' }}>Chargement des données...</div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Collaborateur</th>
                      <th style={styles.th}>Présence</th>
                      <th style={styles.th}>Tâches</th>
                      <th style={styles.th}>Score</th>
                      <th style={styles.th}>Arrivée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => {
                      const tachesPct = emp.tachesQuot > 0 ? Math.round(emp.tachesDone / emp.tachesQuot * 100) : 0;
                      return (
                        <tr key={emp.id}>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: (emp.color || '#C9A84C') + '22', color: emp.color || '#C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{emp.av}</div>
                              <div>
                                <div style={{ fontWeight: 600 }}>{emp.prenom} {emp.nom}</div>
                                <div style={{ fontSize: 11, color: '#8ea0b7' }}>{emp.poste}</div>
                              </div>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span style={{ color: emp.present ? '#66bb6a' : '#ef5350', fontWeight: 600 }}>
                              {emp.present ? '● Présent' : '● Absent'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div>{emp.tachesDone}/{emp.tachesQuot}</div>
                            <Pbar pct={tachesPct} />
                          </td>
                          <td style={styles.td}>
                            <span style={{ color: emp.score >= 8 ? '#66bb6a' : emp.score >= 6 ? '#C9A84C' : '#ef8080', fontWeight: 600 }}>
                              {emp.score}/10
                            </span>
                          </td>
                          <td style={styles.td}>{emp.arrivee || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* TÂCHES DU JOUR */}
          {activeTab === 'taches' && (
            <div style={styles.sectionCard}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>✅ Tâches du jour</div>
              {loading ? <div style={{ color: '#667', padding: 20 }}>Chargement...</div> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
                  {employees.map(emp => {
                    const pct = emp.tachesQuot > 0 ? Math.round(emp.tachesDone / emp.tachesQuot * 100) : 0;
                    return (
                      <div key={emp.id} style={{ background: '#162535', borderRadius: 10, padding: 14, border: '1px solid rgba(255,255,255,.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: (emp.color || '#C9A84C') + '22', color: emp.color || '#C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{emp.av}</div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{emp.prenom} {emp.nom}</div>
                            <div style={{ fontSize: 10, color: '#556' }}>{emp.tachesDone}/{emp.tachesQuot} tâches</div>
                          </div>
                          <div style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: pct >= 70 ? '#66bb6a' : pct >= 40 ? '#ff9800' : '#ef5350' }}>{pct}%</div>
                        </div>
                        <Pbar pct={pct} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PRÉSENCE */}
          {activeTab === 'presence' && (
            <div style={styles.sectionCard}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>📍 Présence & Pointage</div>
              {loading ? <div style={{ color: '#667', padding: 20 }}>Chargement...</div> : (
                <>
                  <div style={{ display: 'flex', gap: 20, marginBottom: 16, fontSize: 12 }}>
                    <span style={{ color: '#66bb6a' }}>✅ Présents : {employees.filter(e => e.present).length}</span>
                    <span style={{ color: '#ffb74d' }}>⏰ Retards : {retardsList.length}</span>
                    <span style={{ color: '#ef5350' }}>❌ Absents : {absents.length}</span>
                  </div>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Collaborateur</th>
                        <th style={styles.th}>Poste</th>
                        <th style={styles.th}>Heure arrivée</th>
                        <th style={styles.th}>Limite</th>
                        <th style={styles.th}>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => {
                        const isRetard = emp.present && emp.arrivee && emp.arrivee !== '—' && emp.arrivee > (emp.heureLimite || '08:30');
                        return (
                          <tr key={emp.id}>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: (emp.color || '#C9A84C') + '22', color: emp.color || '#C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>{emp.av}</div>
                                <span style={{ fontWeight: 600 }}>{emp.prenom} {emp.nom}</span>
                              </div>
                            </td>
                            <td style={styles.td}>{emp.poste}</td>
                            <td style={styles.td}>{emp.arrivee || '—'}</td>
                            <td style={styles.td}>{emp.heureLimite || '08:30'}</td>
                            <td style={styles.td}>
                              {!emp.present
                                ? <span style={{ color: '#ef5350', fontWeight: 600 }}>● Absent</span>
                                : isRetard
                                  ? <span style={{ color: '#ffb74d', fontWeight: 600 }}>⏰ Retard</span>
                                  : <span style={{ color: '#66bb6a', fontWeight: 600 }}>✓ À l'heure</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {/* PERFORMANCE */}
          {activeTab === 'performance' && (
            <div style={styles.sectionCard}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>⭐ Performance & Scores</div>
              {loading ? <div style={{ color: '#667', padding: 20 }}>Chargement...</div> : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Collaborateur</th>
                      <th style={styles.th}>Score</th>
                      <th style={styles.th}>Niveau</th>
                      <th style={styles.th}>Tâches</th>
                      <th style={styles.th}>Objectifs atteints</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...employees].sort((a, b) => (b.score || 0) - (a.score || 0)).map((emp, i) => {
                      const tachesPct = emp.tachesQuot > 0 ? Math.round(emp.tachesDone / emp.tachesQuot * 100) : 0;
                      const objAtteints = (emp.objectifs || []).filter(o => o.statut === 'atteint').length;
                      return (
                        <tr key={emp.id}>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span>{['🥇','🥈','🥉'][i] || `${i+1}.`}</span>
                              <div style={{ width: 24, height: 24, borderRadius: '50%', background: (emp.color || '#C9A84C') + '22', color: emp.color || '#C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>{emp.av}</div>
                              <span style={{ fontWeight: 600 }}>{emp.prenom} {emp.nom}</span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span style={{ fontWeight: 700, fontSize: 15, color: emp.score >= 8 ? '#66bb6a' : emp.score >= 6 ? '#C9A84C' : '#ef8080' }}>{emp.score}/10</span>
                          </td>
                          <td style={styles.td}>{emp.scoreLabel || '—'}</td>
                          <td style={styles.td}><Pbar pct={tachesPct} /></td>
                          <td style={styles.td}>{objAtteints}/{(emp.objectifs || []).length}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* OBJECTIFS */}
          {activeTab === 'objectifs' && (
            <div style={styles.sectionCard}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>🎯 Objectifs par collaborateur</div>
              {loading ? <div style={{ color: '#667', padding: 20 }}>Chargement...</div> : employees.map(emp => (
                <div key={emp.id} style={{ marginBottom: 16, padding: 14, background: '#162535', borderRadius: 10, border: '1px solid rgba(255,255,255,.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: (emp.color || '#C9A84C') + '22', color: emp.color || '#C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{emp.av}</div>
                    <div style={{ fontWeight: 600 }}>{emp.prenom} {emp.nom}</div>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: '#C9A84C' }}>{(emp.objectifs || []).filter(o => o.statut === 'atteint').length}/{(emp.objectifs || []).length} atteints</span>
                  </div>
                  {(emp.objectifs || []).length === 0
                    ? <div style={{ fontSize: 11, color: '#445' }}>Aucun objectif défini</div>
                    : (emp.objectifs || []).map((o, idx) => (
                      <div key={idx} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11 }}>
                          <span style={{ color: '#aaa' }}>{o.titre}</span>
                          <span style={{ fontWeight: 600, color: o.pct >= 80 ? '#66bb6a' : '#C9A84C' }}>{o.pct}%</span>
                        </div>
                        <Pbar pct={o.pct} />
                      </div>
                    ))}
                </div>
              ))}
            </div>
          )}

          {/* ALERTES */}
          {activeTab === 'alertes' && (
            <div style={styles.sectionCard}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>⚠️ Alertes RH</div>
              {loading ? <div style={{ color: '#667', padding: 20 }}>Chargement...</div>
                : alertes.length === 0
                  ? <div style={{ textAlign: 'center', padding: 40, color: '#66bb6a' }}>✅ Aucune alerte active</div>
                  : alertes.map(emp => (
                    <div key={emp.id} style={{ marginBottom: 12, padding: 14, background: 'rgba(239,83,80,.08)', borderRadius: 10, border: '1px solid rgba(239,83,80,.2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#ef535022', color: '#ef8080', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{emp.av}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.prenom} {emp.nom}</div>
                          <div style={{ fontSize: 11, color: '#ef8080', marginTop: 2 }}>
                            {!emp.present && '❌ Absent · '}
                            {emp.score < 6 && `📉 Score faible (${emp.score}/10)`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default TdbEmployesPage;
